/**
 * IndividualDashboard
 *
 * Dashboard for the "individual" role — independent property owners
 * managing up to 5 properties (STR or long-term rental) outside of
 * a strata context.
 *
 * Features:
 *  - Quick-stat cards: properties, active bookings, nights used, documents
 *  - Rental calendar (month-view of bookings) ↔ Bookings table toggle
 *  - Income & expense tracker per property (new for v2)
 *  - Per-property night-usage progress bars
 *  - Documents expiring soon
 *  - Open maintenance items
 *  - Quick-action buttons (including Messages & CSV export)
 */
import { useState, useMemo } from "react";
import {
  Home, CalendarDays, FileCheck, Wrench, AlertTriangle,
  Plus, ArrowRight, TrendingUp, Activity, CheckCircle2,
  Clock, Building2, MessageSquare, Download, DollarSign,
  TrendingDown, Wallet, ChevronLeft, ChevronRight as ChevronRightIcon,
  LayoutGrid, List, Trash2, X, UserPlus,
} from "lucide-react";
import {
  House as PhHouse,
  CalendarDots as PhCalendarDots,
  TrendUp as PhTrendUp,
  Wrench as PhWrench,
} from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { StatCard } from "@/components/StatCard";
import { StatusBadge } from "@/components/StatusBadge";
import { ActivityFeed } from "@/components/ActivityFeed";
import {
  Table, TableBody, TableCell, TableHead,
  TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { useBookings } from "@/hooks/useBookings";
import { useUnits } from "@/hooks/useUnits";
import { useDocuments } from "@/hooks/useDocuments";
import { useMaintenanceRequests } from "@/hooks/useMaintenance";
import { useHosts } from "@/hooks/useHosts";
import { useNavigate } from "react-router-dom";
import {
  differenceInDays, parseISO, isFuture, isToday,
  format, startOfMonth, endOfMonth, eachDayOfInterval,
  getDay, addMonths, subMonths, isSameDay,
} from "date-fns";
import {
  useIndividualFinances,
  INCOME_CATEGORIES,
  EXPENSE_CATEGORIES,
  type FinanceEntryInput,
} from "@/hooks/useIndividualFinances";
import { useToast } from "@/hooks/use-toast";

// ─── Property limit for Individual plan ──────────────────────────────────────
const INDIVIDUAL_PROPERTY_LIMIT = 5;

// ─── CSV export helper ────────────────────────────────────────────────────────
function exportCSV(
  units: any[],
  bookings: any[],
  incomeTotal: number,
  expenseTotal: number,
) {
  const rows = [
    ["Property", "Bookings", "Total Nights", "Status"],
    ...units.map((u) => {
      const bks = bookings.filter((b: any) => b.unit_id === u.id);
      const nights = bks.reduce((s: number, b: any) => s + (b.nights ?? 0), 0);
      return [
        u.unit_number ?? "Unknown",
        bks.length,
        nights,
        u.status ?? "active",
      ];
    }),
    [],
    ["Summary", "", "", ""],
    ["Total Income", `$${incomeTotal.toFixed(2)}`, "", ""],
    ["Total Expenses", `$${expenseTotal.toFixed(2)}`, "", ""],
    ["Net Balance", `$${(incomeTotal - expenseTotal).toFixed(2)}`, "", ""],
  ];
  const csv = rows.map((r) => r.join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `property-report-${format(new Date(), "yyyy-MM-dd")}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Mini Rental Calendar ─────────────────────────────────────────────────────
function RentalCalendar({ bookings }: { bookings: any[] }) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const start = startOfMonth(currentMonth);
  const end = endOfMonth(currentMonth);
  const days = eachDayOfInterval({ start, end });
  const startWeekday = getDay(start); // 0=Sun

  // Build a set of booked dates
  const bookedDates = useMemo(() => {
    const set = new Set<string>();
    bookings.forEach((b) => {
      try {
        const cin = parseISO(b.check_in);
        const cout = parseISO(b.check_out ?? b.check_in);
        eachDayOfInterval({ start: cin, end: cout }).forEach((d) =>
          set.add(format(d, "yyyy-MM-dd")),
        );
      } catch { /* skip malformed dates */ }
    });
    return set;
  }, [bookings]);

  const today = new Date();

  return (
    <div className="space-y-3">
      {/* Month navigation */}
      <div className="flex items-center justify-between">
        <button
          className="p-1 rounded hover:bg-muted transition-colors"
          onClick={() => setCurrentMonth((m) => subMonths(m, 1))}
          aria-label="Previous month"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="text-sm font-semibold">
          {format(currentMonth, "MMMM yyyy")}
        </span>
        <button
          className="p-1 rounded hover:bg-muted transition-colors"
          onClick={() => setCurrentMonth((m) => addMonths(m, 1))}
          aria-label="Next month"
        >
          <ChevronRightIcon className="h-4 w-4" />
        </button>
      </div>

      {/* Day-of-week headers */}
      <div className="grid grid-cols-7 gap-px text-center">
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
          <div key={i} className="text-[10px] font-bold text-muted-foreground py-1">
            {d}
          </div>
        ))}

        {/* Empty cells before month starts */}
        {Array.from({ length: startWeekday }).map((_, i) => (
          <div key={`empty-${i}`} />
        ))}

        {/* Day cells */}
        {days.map((day) => {
          const key = format(day, "yyyy-MM-dd");
          const isBooked = bookedDates.has(key);
          const isTdy = isSameDay(day, today);
          return (
            <div
              key={key}
              className={`
                aspect-square flex items-center justify-center rounded text-[11px] font-medium
                ${isBooked ? "bg-primary/20 text-primary font-bold" : ""}
                ${isTdy ? "ring-1 ring-primary ring-offset-1" : ""}
                ${!isBooked && !isTdy ? "text-muted-foreground hover:bg-muted/50" : ""}
              `}
            >
              {format(day, "d")}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-3 text-[10px] text-muted-foreground pt-1">
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded bg-primary/20" />
          Booked
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded ring-1 ring-primary" />
          Today
        </span>
      </div>
    </div>
  );
}

// ─── Add Finance Entry Form ───────────────────────────────────────────────────
function AddFinanceForm({
  units,
  onAdd,
  isLoading,
}: {
  units: any[];
  onAdd: (input: FinanceEntryInput) => void;
  isLoading: boolean;
}) {
  const [type, setType] = useState<"income" | "expense">("income");
  const [category, setCategory] = useState("");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [notes, setNotes] = useState("");
  const [unitId, setUnitId] = useState<string>("");
  const [open, setOpen] = useState(false);

  const categories = type === "income"
    ? INCOME_CATEGORIES
    : EXPENSE_CATEGORIES;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!category || !amount) return;
    onAdd({
      type,
      category,
      amount: parseFloat(amount),
      entry_date: date,
      notes: notes || null,
      unit_id: unitId || null,
    });
    setAmount("");
    setNotes("");
    setOpen(false);
  };

  if (!open) {
    return (
      <Button
        size="sm"
        variant="outline"
        className="w-full border-dashed"
        onClick={() => setOpen(true)}
      >
        <Plus className="mr-1.5 h-3.5 w-3.5" />
        Add Income / Expense
      </Button>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3 rounded-lg border p-3 bg-muted/30">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold">New Entry</p>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-muted-foreground hover:text-foreground"
          aria-label="Close"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Type toggle */}
      <div className="flex gap-1.5">
        {(["income", "expense"] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => { setType(t); setCategory(""); }}
            className={`flex-1 py-1 rounded text-xs font-semibold transition-colors ${
              type === t
                ? t === "income"
                  ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400"
                  : "bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-400"
                : "bg-muted text-muted-foreground hover:bg-muted/80"
            }`}
          >
            {t === "income" ? "Income" : "Expense"}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[10px] text-muted-foreground">Category</Label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="h-7 text-xs">
              <SelectValue placeholder="Select…" />
            </SelectTrigger>
            <SelectContent>
              {categories.map((c) => (
                <SelectItem key={c} value={c} className="text-xs">{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] text-muted-foreground">Amount ($)</Label>
          <Input
            type="number"
            min="0"
            step="0.01"
            placeholder="0.00"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="h-7 text-xs"
            required
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[10px] text-muted-foreground">Date</Label>
          <Input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="h-7 text-xs"
          />
        </div>
        <div>
          <Label className="text-[10px] text-muted-foreground">Property</Label>
          <Select value={unitId} onValueChange={setUnitId}>
            <SelectTrigger className="h-7 text-xs">
              <SelectValue placeholder="All" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="" className="text-xs">All Properties</SelectItem>
              {units.map((u) => (
                <SelectItem key={u.id} value={u.id} className="text-xs">
                  {u.unit_number}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label className="text-[10px] text-muted-foreground">Notes (optional)</Label>
        <Input
          placeholder="e.g. January rent from tenant"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="h-7 text-xs"
        />
      </div>

      <Button type="submit" size="sm" className="w-full h-7 text-xs" disabled={isLoading}>
        {isLoading ? "Saving…" : "Save Entry"}
      </Button>
    </form>
  );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────
export default function IndividualDashboard() {
  const { user, activeOrgId } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [bookingView, setBookingView] = useState<"table" | "calendar">("table");

  const { data: units = [], isLoading: unitsLoading } = useUnits();
  const { data: bookings = [], isLoading: bookingsLoading } = useBookings();
  const { data: hosts = [], isLoading: hostsLoading } = useHosts();
  const { data: maintenance = [], isLoading: maintenanceLoading } = useMaintenanceRequests("all");

  const myHost = hosts.find((h: any) => h.user_id === user?.id);
  const { data: docs = [], isLoading: docsLoading } = useDocuments(myHost?.id);

  const {
    entries: financeEntries,
    isLoading: financesLoading,
    totalIncome,
    totalExpenses,
    netBalance,
    add: addFinance,
    remove: removeFinance,
  } = useIndividualFinances();

  const isLoading = unitsLoading || bookingsLoading || hostsLoading ||
    maintenanceLoading || docsLoading || financesLoading;

  // ── Derived stats ─────────────────────────────────────────────────────────
  const myBookings = myHost
    ? bookings.filter((b: any) => b.host_id === myHost.id)
    : [];

  const activeBookings = myBookings.filter((b: any) => b.status === "approved");
  const upcomingBookings = activeBookings
    .filter((b: any) => isFuture(parseISO(b.check_in)) || isToday(parseISO(b.check_in)))
    .sort((a: any, b: any) => a.check_in.localeCompare(b.check_in))
    .slice(0, 5);

  const recentBookings = myBookings
    .sort((a: any, b: any) => b.created_at.localeCompare(a.created_at))
    .slice(0, 6);

  const expiringDocs = docs.filter((d: any) => {
    if (!d.expires_at) return false;
    const days = differenceInDays(parseISO(d.expires_at), new Date());
    return days >= 0 && days <= 60;
  });

  const openMaintenance = maintenance.filter(
    (m: any) => m.status !== "completed" && m.status !== "closed",
  );

  const nightsUsed = myHost?.nights_used ?? 0;
  const nightLimit = myHost?.night_limit ?? 365;
  const nightPercent = nightLimit > 0
    ? Math.min(Math.round((nightsUsed / nightLimit) * 100), 100)
    : 0;
  const nightBarColor =
    nightPercent >= 90 ? "bg-destructive" :
    nightPercent >= 75 ? "bg-amber-500" :
    "bg-primary";

  const propertyCount = units.length;
  const canAddProperty = propertyCount < INDIVIDUAL_PROPERTY_LIMIT;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-72" />
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <Skeleton className="h-64 lg:col-span-2" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  if (!activeOrgId) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-5 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
          <Building2 className="h-8 w-8 text-muted-foreground" />
        </div>
        <div className="space-y-2 max-w-sm">
          <h2 className="text-xl font-semibold">Setting up your workspace</h2>
          <p className="text-sm text-muted-foreground">
            Your personal property workspace is being created. This usually completes
            in a few seconds — try refreshing the page.
          </p>
        </div>
        <Button onClick={() => window.location.reload()}>
          Refresh Page
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">My Property Dashboard</h1>
          <p className="text-sm text-muted-foreground">
            Individual Property Owner · {propertyCount} of {INDIVIDUAL_PROPERTY_LIMIT} properties
          </p>
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          <Button
            size="sm"
            variant="outline"
            onClick={() => exportCSV(units, myBookings, totalIncome, totalExpenses)}
          >
            <Download className="mr-1.5 h-3.5 w-3.5" /> Export Report
          </Button>
          <Button size="sm" variant="outline" onClick={() => navigate("/bookings")}>
            <CalendarDays className="mr-1.5 h-3.5 w-3.5" /> New Booking
          </Button>
          {canAddProperty && (
            <Button size="sm" onClick={() => navigate("/units")}>
              <Plus className="mr-1.5 h-3.5 w-3.5" /> Add Property
            </Button>
          )}
        </div>
      </div>

      {/* ── Property limit banner ── */}
      {!canAddProperty && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-center gap-2">
          <Building2 className="h-4 w-4 shrink-0" />
          <span>
            You've reached the <strong>5-property limit</strong> on the Individual Plan.
            Upgrade to Property Manager for unlimited buildings.
          </span>
        </div>
      )}

      {/* ── Expiring documents alert ── */}
      {expiringDocs.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <FileCheck className="h-4 w-4 shrink-0" />
            <span>
              <strong>{expiringDocs.length} document{expiringDocs.length > 1 ? "s" : ""}</strong> expiring within 60 days.
            </span>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="border-amber-400 text-amber-800 hover:bg-amber-100 shrink-0"
            onClick={() => navigate("/my-documents")}
          >
            View Documents
          </Button>
        </div>
      )}

      {/* ── Stat cards ── */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard
          title="My Properties"
          value={`${propertyCount} / ${INDIVIDUAL_PROPERTY_LIMIT}`}
          icon={PhHouse}
          iconWeight="duotone"
          variant="default"
        />
        <StatCard
          title="Active Bookings"
          value={activeBookings.length}
          icon={PhCalendarDots}
          iconWeight="duotone"
          variant="success"
        />
        <StatCard
          title="Nights Used"
          value={`${nightsUsed} / ${nightLimit}`}
          icon={PhTrendUp}
          iconWeight="duotone"
          variant={nightPercent >= 90 ? "warning" : "info"}
        />
        <StatCard
          title="Open Maintenance"
          value={openMaintenance.length}
          icon={PhWrench}
          iconWeight="duotone"
          variant={openMaintenance.length > 0 ? "warning" : "default"}
        />
      </div>

      {/* ── Financial summary cards ── */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="border-emerald-200 dark:border-emerald-500/30">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-2 mb-1">
              <DollarSign className="h-4 w-4 text-emerald-600" />
              <p className="text-xs font-medium text-muted-foreground">Total Income</p>
            </div>
            <p className="text-xl font-bold text-emerald-600">
              ${totalIncome.toLocaleString("en-CA", { minimumFractionDigits: 2 })}
            </p>
          </CardContent>
        </Card>
        <Card className="border-rose-200 dark:border-rose-500/30">
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-2 mb-1">
              <TrendingDown className="h-4 w-4 text-rose-600" />
              <p className="text-xs font-medium text-muted-foreground">Total Expenses</p>
            </div>
            <p className="text-xl font-bold text-rose-600">
              ${totalExpenses.toLocaleString("en-CA", { minimumFractionDigits: 2 })}
            </p>
          </CardContent>
        </Card>
        <Card className={netBalance >= 0 ? "border-emerald-200 dark:border-emerald-500/30" : "border-rose-200 dark:border-rose-500/30"}>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-center gap-2 mb-1">
              <Wallet className="h-4 w-4 text-muted-foreground" />
              <p className="text-xs font-medium text-muted-foreground">Net Balance</p>
            </div>
            <p className={`text-xl font-bold ${netBalance >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
              {netBalance < 0 ? "-" : ""}${Math.abs(netBalance).toLocaleString("en-CA", { minimumFractionDigits: 2 })}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* ── Main content grid ── */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">

        {/* ── Left: Bookings table / calendar ── */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold">Bookings</CardTitle>
              <div className="flex items-center gap-1">
                {/* View toggle */}
                <button
                  onClick={() => setBookingView("table")}
                  className={`p-1.5 rounded ${bookingView === "table" ? "bg-muted" : "hover:bg-muted/50"} transition-colors`}
                  title="Table view"
                >
                  <List className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => setBookingView("calendar")}
                  className={`p-1.5 rounded ${bookingView === "calendar" ? "bg-muted" : "hover:bg-muted/50"} transition-colors`}
                  title="Calendar view"
                >
                  <LayoutGrid className="h-3.5 w-3.5" />
                </button>
                <Button
                  variant="ghost" size="sm" className="h-7 text-xs ml-1"
                  onClick={() => navigate("/bookings")}
                >
                  View all <ArrowRight className="ml-1 h-3 w-3" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {bookingView === "calendar" ? (
              <RentalCalendar bookings={myBookings} />
            ) : recentBookings.length === 0 ? (
              <div className="flex flex-col items-center gap-3 py-8 text-center">
                <CalendarDays className="h-8 w-8 text-muted-foreground/40" />
                <p className="text-sm text-muted-foreground">No bookings yet. Add your first booking.</p>
                <Button size="sm" onClick={() => navigate("/bookings")}>
                  <Plus className="mr-1.5 h-3.5 w-3.5" /> New Booking
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Guest</TableHead>
                    <TableHead className="text-xs">Property</TableHead>
                    <TableHead className="text-xs">Check-in</TableHead>
                    <TableHead className="text-xs">Nights</TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentBookings.map((b: any) => (
                    <TableRow key={b.id} className={isToday(parseISO(b.check_in)) ? "bg-primary/5" : ""}>
                      <TableCell className="text-sm font-medium">{b.guest_name}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {b.unit?.unit_number ? `Unit ${b.unit.unit_number}` : "—"}
                      </TableCell>
                      <TableCell className="text-xs">
                        {b.check_in}
                        {isToday(parseISO(b.check_in)) && (
                          <Badge variant="outline" className="ml-1 text-[9px] px-1 border-primary/40 text-primary">
                            Today
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">{b.nights}</TableCell>
                      <TableCell><StatusBadge status={b.status || "approved"} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* ── Right column ── */}
        <div className="space-y-4">

          {/* Night usage */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
                Night Usage
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {myHost ? (
                <>
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">Used this year</span>
                    <span className="font-medium">{nightsUsed} / {nightLimit}</span>
                  </div>
                  <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${nightBarColor}`}
                      style={{ width: `${nightPercent}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className={nightPercent >= 90 ? "text-destructive font-medium" : "text-muted-foreground"}>
                      {nightPercent}% used
                    </span>
                    <span className="text-muted-foreground">{nightLimit - nightsUsed} remaining</span>
                  </div>
                  {nightPercent >= 90 && (
                    <p className="text-[11px] text-destructive bg-destructive/5 rounded p-2">
                      Approaching annual night limit. Review your upcoming bookings.
                    </p>
                  )}
                </>
              ) : (
                <p className="text-xs text-muted-foreground text-center py-3">
                  Add a property to start tracking nights.
                </p>
              )}
            </CardContent>
          </Card>

          {/* My Properties list */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Home className="h-4 w-4 text-muted-foreground" />
                  My Properties
                </CardTitle>
                {canAddProperty && (
                  <Button variant="ghost" size="sm" className="h-6 text-xs px-2" onClick={() => navigate("/units")}>
                    <Plus className="h-3 w-3 mr-1" /> Add
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {units.length === 0 ? (
                <div className="flex flex-col items-center gap-2 py-4 text-center">
                  <Home className="h-6 w-6 text-muted-foreground/40" />
                  <p className="text-xs text-muted-foreground">No properties yet.</p>
                  <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => navigate("/units")}>
                    Add Your First Property
                  </Button>
                </div>
              ) : (
                <ul className="space-y-2">
                  {units.map((u: any) => (
                    <li key={u.id} className="flex items-center justify-between rounded-md border p-2.5 hover:bg-muted/40 transition-colors">
                      <div>
                        <p className="text-xs font-semibold">{u.unit_number}</p>
                        {u.bedrooms != null && (
                          <p className="text-[10px] text-muted-foreground">
                            {u.bedrooms} bed · {u.bathrooms ?? "?"} bath
                          </p>
                        )}
                      </div>
                      <Badge
                        variant={u.status === "active" ? "outline" : "secondary"}
                        className={`text-[10px] px-1.5 ${u.status === "active" ? "border-emerald-400 text-emerald-700" : ""}`}
                      >
                        {u.status}
                      </Badge>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          {/* Upcoming check-ins */}
          {upcomingBookings.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold">Upcoming Check-ins</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {upcomingBookings.map((b: any) => (
                  <div key={b.id} className="flex justify-between items-center text-xs">
                    <span className="font-medium truncate">{b.guest_name}</span>
                    <span className="text-muted-foreground shrink-0 ml-2">{b.check_in}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* ── Income & Expense Tracker ── */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Wallet className="h-4 w-4 text-muted-foreground" />
              Income & Expenses
            </CardTitle>
            <Button
              variant="ghost" size="sm" className="h-7 text-xs"
              onClick={() => exportCSV(units, myBookings, totalIncome, totalExpenses)}
            >
              <Download className="mr-1 h-3 w-3" /> CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Add form */}
          <AddFinanceForm
            units={units}
            isLoading={addFinance.isPending}
            onAdd={(input) => {
              addFinance.mutate(input, {
                onSuccess: () =>
                  toast({ title: "Entry saved", description: `${input.type === "income" ? "Income" : "Expense"} of $${input.amount} recorded.` }),
              });
            }}
          />

          {/* Recent entries */}
          {financeEntries.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-4">
              No entries yet. Add your first income or expense above.
            </p>
          ) : (
            <div className="space-y-1.5 max-h-60 overflow-y-auto pr-1">
              {financeEntries.slice(0, 10).map((e) => (
                <div
                  key={e.id}
                  className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-muted/40 transition-colors group"
                >
                  <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${e.type === "income" ? "bg-emerald-500" : "bg-rose-500"}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate">{e.category}</p>
                    <p className="text-[10px] text-muted-foreground">
                      {e.entry_date}{e.unit ? ` · ${e.unit.unit_number}` : ""}{e.notes ? ` · ${e.notes}` : ""}
                    </p>
                  </div>
                  <span className={`text-xs font-semibold shrink-0 ${e.type === "income" ? "text-emerald-600" : "text-rose-600"}`}>
                    {e.type === "income" ? "+" : "-"}${e.amount.toFixed(2)}
                  </span>
                  <button
                    onClick={() => removeFinance.mutate(e.id)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                    aria-label="Remove entry"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Bottom row: Maintenance + Documents + Activity ── */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">

        {/* Maintenance */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Wrench className="h-4 w-4 text-muted-foreground" />
                Maintenance
                {openMaintenance.length > 0 && (
                  <Badge variant="destructive" className="text-[10px] h-4 px-1.5">
                    {openMaintenance.length} open
                  </Badge>
                )}
              </CardTitle>
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => navigate("/maintenance")}>
                View all <ArrowRight className="ml-1 h-3 w-3" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {openMaintenance.length === 0 ? (
              <div className="flex items-center gap-2 py-4 justify-center">
                <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                <p className="text-xs text-muted-foreground">No open maintenance items.</p>
              </div>
            ) : (
              <ul className="space-y-2">
                {openMaintenance.slice(0, 4).map((m: any) => (
                  <li key={m.id} className="flex items-start gap-2.5 text-xs">
                    <div className={`mt-0.5 h-2 w-2 rounded-full shrink-0 ${
                      m.priority === "urgent" ? "bg-destructive" :
                      m.priority === "high" ? "bg-amber-500" : "bg-blue-400"
                    }`} />
                    <div className="min-w-0">
                      <p className="font-medium truncate">{m.title ?? m.description ?? "Maintenance item"}</p>
                      <p className="text-muted-foreground capitalize">{m.status}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Documents */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <FileCheck className="h-4 w-4 text-muted-foreground" />
                Documents
                {expiringDocs.length > 0 && (
                  <Badge variant="outline" className="text-[10px] h-4 px-1.5 border-amber-400 text-amber-700">
                    {expiringDocs.length} expiring
                  </Badge>
                )}
              </CardTitle>
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => navigate("/my-documents")}>
                View all <ArrowRight className="ml-1 h-3 w-3" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {docs.length === 0 ? (
              <div className="flex flex-col items-center gap-2 py-4 text-center">
                <FileCheck className="h-6 w-6 text-muted-foreground/40" />
                <p className="text-xs text-muted-foreground">No documents uploaded.</p>
                <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => navigate("/my-documents")}>
                  Upload Document
                </Button>
              </div>
            ) : (
              <ul className="space-y-2">
                {docs.slice(0, 4).map((d: any) => {
                  const daysLeft = d.expires_at
                    ? differenceInDays(parseISO(d.expires_at), new Date())
                    : null;
                  return (
                    <li key={d.id} className="flex items-center justify-between text-xs">
                      <span className="font-medium truncate max-w-[60%]">{d.name}</span>
                      <div className="flex items-center gap-1.5">
                        {daysLeft !== null && daysLeft <= 60 && (
                          <Badge
                            variant="outline"
                            className={`text-[10px] h-4 px-1.5 ${daysLeft <= 14 ? "border-destructive text-destructive" : "border-amber-400 text-amber-700"}`}
                          >
                            {daysLeft}d
                          </Badge>
                        )}
                        <StatusBadge status={d.status} />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Activity feed */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Activity className="h-4 w-4 text-muted-foreground" />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ActivityFeed
              orgId={activeOrgId}
              compact
              limit={6}
              emptyMessage="No recent activity."
              filters={user ? { userId: user.id } : {}}
            />
          </CardContent>
        </Card>
      </div>

      {/* ── Quick actions ── */}
      <Card className="bg-muted/30">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => navigate("/bookings")}>
              <CalendarDays className="mr-1.5 h-3.5 w-3.5" /> New Booking
            </Button>
            <Button size="sm" variant="outline" onClick={() => navigate("/my-documents")}>
              <FileCheck className="mr-1.5 h-3.5 w-3.5" /> Upload Document
            </Button>
            <Button size="sm" variant="outline" onClick={() => navigate("/maintenance")}>
              <Wrench className="mr-1.5 h-3.5 w-3.5" /> Log Maintenance
            </Button>
            <Button size="sm" variant="outline" onClick={() => navigate("/disputes")}>
              <AlertTriangle className="mr-1.5 h-3.5 w-3.5" /> Log Dispute
            </Button>
            <Button size="sm" variant="outline" onClick={() => navigate("/messages")}>
              <MessageSquare className="mr-1.5 h-3.5 w-3.5" /> Messages
            </Button>
            <Button size="sm" variant="outline" onClick={() => navigate("/my-invitations")}>
              <UserPlus className="mr-1.5 h-3.5 w-3.5" /> Invite Someone
            </Button>
            <Button size="sm" variant="outline" onClick={() => exportCSV(units, myBookings, totalIncome, totalExpenses)}>
              <Download className="mr-1.5 h-3.5 w-3.5" /> Export CSV
            </Button>
            {canAddProperty && (
              <Button size="sm" variant="outline" onClick={() => navigate("/units")}>
                <Plus className="mr-1.5 h-3.5 w-3.5" /> Add Property
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
